const FALLBACK_ICE_CONFIG = {
  iceServers: [
    { urls: ['stun:stun.cloudflare.com:3478'] },
  ],
  iceTransportPolicy: 'all',
};

const ICE_GATHERING_TIMEOUT = 5000;

export class WebRTCManager {
  constructor() {
    this.pc = null;
    this.localStream = null;
    this.remoteStream = null;
    this.onRemoteStream = null;
    this.onIceCandidate = null;
    this.onIceRestart = null;
    this.onConnectionState = null;
    this.iceGatheringTimeout = null;
    this.pendingIceCandidates = [];
    this.hasRemoteDescription = false;
    this.tracksAdded = false;
    this.isOfferHandled = false;
    this.isAnswerHandled = false;
    this.iceConfigPromise = null;
  }

  async getIceConfig() {
    if (!this.iceConfigPromise) {
      this.iceConfigPromise = fetch('/api/ice-servers')
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`ICE config request failed with ${response.status}`);
          }

          const config = await response.json();
          return {
            ...FALLBACK_ICE_CONFIG,
            ...config,
          };
        })
        .catch((error) => {
          console.warn('Falling back to STUN-only ICE config:', error);
          return FALLBACK_ICE_CONFIG;
        });
    }

    return this.iceConfigPromise;
  }

  async initLocalStream() {
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      console.log('Local stream obtained');
      return this.localStream;
    } catch (error) {
      console.error('Failed to get local stream:', error);
      throw error;
    }
  }

  async createPeerConnection() {
    // Close existing PC if any
    if (this.pc) {
      console.log('Closing existing peer connection');
      this.pc.close();
    }

    const iceConfig = await this.getIceConfig();

    this.pc = new RTCPeerConnection(iceConfig);
    this.hasRemoteDescription = false;
    this.tracksAdded = false;
    this.isOfferHandled = false;
    this.isAnswerHandled = false;
    
    console.log('Peer connection created');

    this.pc.onicecandidate = (event) => {
      if (event.candidate && this.onIceCandidate) {
        console.log('Local ICE candidate generated');
        this.onIceCandidate(event.candidate);
      }
    };

    this.pc.ontrack = (event) => {
      if (event.streams && event.streams[0]) {
        console.log('Remote track received');
        this.remoteStream = event.streams[0];
        if (this.onRemoteStream) {
          this.onRemoteStream(this.remoteStream);
        }
      }
    };

    this.pc.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', this.pc.iceConnectionState);

      if (this.pc.iceConnectionState === 'connected' || this.pc.iceConnectionState === 'completed') {
        this.onConnectionState?.('connected');
      }

      if (this.pc.iceConnectionState === 'failed') {
        this.handleIceFailure();
      }
    };

    this.pc.onconnectionstatechange = () => {
      console.log('Connection state:', this.pc.connectionState);
      this.onConnectionState?.(this.pc.connectionState);
    };

    this.pc.onsignalingstatechange = () => {
      console.log('Signaling state:', this.pc.signalingState);
    };
  }

  addLocalTracks() {
    if (!this.pc || !this.localStream) {
      console.warn('Cannot add tracks - PC or stream not ready');
      return;
    }
    
    if (this.tracksAdded) {
      console.log('Tracks already added, skipping');
      return;
    }
    
    console.log('Adding local tracks to peer connection');
    this.localStream.getTracks().forEach((track) => {
      console.log('Adding track:', track.kind);
      this.pc.addTrack(track, this.localStream);
    });
    this.tracksAdded = true;
  }

  async createOffer() {
    // Always create new PC for caller
    await this.createPeerConnection();
    this.addLocalTracks();

    try {
      console.log('Creating offer...');
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      console.log('Offer created, SDP length:', this.pc.localDescription.sdp?.length);
      return this.pc.localDescription;
    } catch (error) {
      console.error('Failed to create offer:', error);
      throw error;
    }
  }

  async handleOffer(offer) {
    // Prevent handling offer twice
    if (this.isOfferHandled) {
      console.warn('Offer already handled, skipping');
      return null;
    }
    this.isOfferHandled = true;
    
    // Create new PC for callee
    await this.createPeerConnection();
    this.addLocalTracks();

    try {
      console.log('Handling offer...');
      console.log('Offer type:', offer.type);
      await this.pc.setRemoteDescription(offer);
      this.hasRemoteDescription = true;
      
      // Add any pending ICE candidates
      await this.addPendingIceCandidates();
      
      const answer = await this.pc.createAnswer();
      await this.pc.setLocalDescription(answer);
      console.log('Answer created, SDP length:', this.pc.localDescription.sdp?.length);
      return this.pc.localDescription;
    } catch (error) {
      console.error('Failed to handle offer:', error);
      this.isOfferHandled = false; // Reset on error so we can retry
      throw error;
    }
  }

  async handleAnswer(answer) {
    // Prevent handling answer twice
    if (this.isAnswerHandled) {
      console.warn('Answer already handled, skipping');
      return;
    }
    this.isAnswerHandled = true;
    
    if (!this.pc) {
      console.error('No peer connection when handling answer');
      return;
    }

    try {
      console.log('Handling answer...');
      console.log('Answer type:', answer.type);
      await this.pc.setRemoteDescription(answer);
      this.hasRemoteDescription = true;
      
      // Add any pending ICE candidates
      await this.addPendingIceCandidates();
      
      console.log('Answer set as remote description');
    } catch (error) {
      console.error('Failed to handle answer:', error);
      this.isAnswerHandled = false; // Reset on error so we can retry
      throw error;
    }
  }

  async addIceCandidate(candidate) {
    if (!this.pc) {
      console.log('Queuing ICE candidate (no PC yet)');
      this.pendingIceCandidates.push(candidate);
      return;
    }
    
    if (!this.hasRemoteDescription) {
      console.log('Queuing ICE candidate (no remote description yet)');
      this.pendingIceCandidates.push(candidate);
      return;
    }

    try {
      await this.pc.addIceCandidate(candidate);
      console.log('ICE candidate added');
    } catch (error) {
      console.error('Failed to add ICE candidate:', error);
    }
  }

  async addPendingIceCandidates() {
    if (this.pendingIceCandidates.length === 0 || !this.pc) return;
    
    console.log(`Adding ${this.pendingIceCandidates.length} pending ICE candidates`);
    
    for (const candidate of this.pendingIceCandidates) {
      try {
        await this.pc.addIceCandidate(candidate);
        console.log('Pending ICE candidate added');
      } catch (error) {
        console.error('Failed to add pending ICE candidate:', error);
      }
    }
    
    this.pendingIceCandidates = [];
  }

  waitForIceGathering() {
    return new Promise((resolve) => {
      if (!this.pc || this.pc.iceGatheringState === 'complete') {
        console.log('ICE gathering already complete or PC closed');
        resolve();
        return;
      }

      const checkState = () => {
        if (this.pc && this.pc.iceGatheringState === 'complete') {
          clearTimeout(this.iceGatheringTimeout);
          this.pc.removeEventListener('icegatheringstatechange', checkState);
          console.log('ICE gathering complete');
          resolve();
        }
      };

      this.pc.addEventListener('icegatheringstatechange', checkState);

      this.iceGatheringTimeout = setTimeout(() => {
        if (this.pc) {
          this.pc.removeEventListener('icegatheringstatechange', checkState);
        }
        console.log('ICE gathering timeout (proceeding anyway)');
        resolve();
      }, ICE_GATHERING_TIMEOUT);
    });
  }

  async handleIceFailure() {
    console.log('ICE connection failed, attempting restart...');
    
    if (!this.pc) {
      console.log('No PC to restart');
      return;
    }
    
    try {
      await this.pc.restartIce();
      
      const offer = await this.pc.createOffer({ iceRestart: true });
      await this.pc.setLocalDescription(offer);
      
      await this.waitForIceGathering();
      
      if (this.onIceRestart) {
        this.onIceRestart(this.pc.localDescription);
      }
    } catch (error) {
      console.error('ICE restart failed:', error);
    }
  }

  async restartIce() {
    if (!this.pc) {
      console.log('No peer connection to restart');
      return;
    }

    console.log('Restarting ICE...');
    
    try {
      // Use ICE restart to regenerate candidates
      await this.pc.restartIce();
      
      // Wait a bit for new candidates
      await this.waitForIceGathering();
      
      console.log('ICE restarted');
    } catch (error) {
      console.error('Failed to restart ICE:', error);
      throw error;
    }
  }

  close() {
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.localStream = null;
    }

    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }

    if (this.iceGatheringTimeout) {
      clearTimeout(this.iceGatheringTimeout);
      this.iceGatheringTimeout = null;
    }

    this.remoteStream = null;
    this.pendingIceCandidates = [];
    this.hasRemoteDescription = false;
    this.tracksAdded = false;
    this.isOfferHandled = false;
    this.isAnswerHandled = false;
  }
}
